/* TESSI product gallery: swipe the main photo on a phone.
 *
 * Task 1 of the 2026-07-30 overnight mobile pass. Dragging left or right across the main
 * product photo moves to the next or previous photo, and the thumbnail strip follows.
 *
 * ── WHY IT IS BUILT THIS WAY ────────────────────────────────────────────────────────
 *
 * 1. IT CLICKS THE THUMBNAIL RATHER THAN SETTING THE IMAGE ITSELF.
 *    Each product section owns a `setMain(src)` that does more than swap a src: it looks
 *    the photo up in that page's PHOTO_CROPS table and applies an object-position, an
 *    object-fit, a translate and a zoom. `applySet` then rewrites every thumb's data-src
 *    when the colourway changes. Reproducing any of that here would be a second copy of
 *    the rules, free to drift. Dispatching a click on the target thumb reuses the page's
 *    own handler, so a swipe and a tap are by construction identical, and this file needs
 *    no knowledge of crops, colourways or which product it is on.
 *
 * 2. THE LISTENERS ARE DELEGATED TO `document`, NOT BOUND TO THE GALLERY.
 *    Shopify re-renders a section's HTML when a setting is edited in the theme editor,
 *    which throws away any listener bound to an element inside it. The same trap is
 *    already documented for tessi-hero-video and tessi-why-switch, which solved it by
 *    becoming custom elements. Delegation gets the same durability without adding a
 *    wrapper tag to five product sections.
 *
 * 3. VERTICAL SCROLLING IS PROTECTED BY CSS, NOT BY preventDefault.
 *    `touch-action: pan-y` on the photo (set in snippets/tessi-product-details.liquid)
 *    tells the browser it owns vertical panning and we own horizontal. That means every
 *    listener here can stay PASSIVE, so the page never blocks the scroll thread. A
 *    preventDefault-based version has to be non-passive and risks eating a vertical drag
 *    that started with a few pixels of horizontal wobble.
 *
 * ⚠️ MOBILE ONLY, checked at gesture time rather than at load. Binding once behind a
 * matchMedia check would leave a phone that rotates into a tablet width still swiping.
 */
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 767px)';
  /* 40px of travel before a drag counts. Below about 30 an ordinary tap with a slightly
     rolling thumb starts changing the photo, which reads as the page glitching. */
  var DISTANCE = 40;
  /* Horizontal has to beat vertical by this factor. Straight-down scrolls carry a few
     pixels of sideways drift and must never be read as a swipe. */
  var SLOPE = 1.2;

  var startX = 0;
  var startY = 0;
  var tracking = false;

  function onPhone() {
    return window.matchMedia && window.matchMedia(MOBILE_QUERY).matches;
  }

  /* The gesture only counts when it starts ON the main photo. Starting it on the thumb
     strip must be left alone: that strip is itself a horizontal scroller. */
  function startedOnMainPhoto(target) {
    if (!target || !target.closest) return false;
    return !!target.closest('#tessi-product-main-img');
  }

  /* ⚠️ VISIBLE thumbs only. `applySet` hides the surplus slots with `display: none` when a
     colourway has fewer photos than the strip has slots, so counting all of them would
     swipe into blank frames. `offsetParent === null` catches a hidden element and every
     hidden ancestor with it. */
  function visibleThumbs() {
    var all = document.querySelectorAll('#TESSI-product .tessi-thumb');
    var out = [];
    for (var i = 0; i < all.length; i++) {
      if (all[i].offsetParent !== null) out.push(all[i]);
    }
    return out;
  }

  function activeIndex(thumbs) {
    for (var i = 0; i < thumbs.length; i++) {
      if (thumbs[i].classList.contains('active')) return i;
    }
    return 0;
  }

  function go(direction) {
    var thumbs = visibleThumbs();
    if (thumbs.length < 2) return;

    /* Wraps rather than stopping at the ends: the brief says the swipe should CYCLE
       through the photos, and on a phone a dead end reads as a broken gesture. */
    var next = (activeIndex(thumbs) + direction + thumbs.length) % thumbs.length;
    var target = thumbs[next];
    if (!target) return;

    target.click();

    /* Keep the active thumb in view. The strip is a horizontal overflow scroller on a
       phone, so after two or three swipes the active one is off screen otherwise.
       `nearest` + `center` so it never scrolls the PAGE, only the strip. */
    if (target.scrollIntoView) {
      try {
        target.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
      } catch (e) {
        target.scrollIntoView(false);
      }
    }
  }

  document.addEventListener('touchstart', function (e) {
    tracking = false;
    if (!onPhone()) return;
    if (!e.touches || e.touches.length !== 1) return;      /* ignore pinch-zoom */
    if (!startedOnMainPhoto(e.target)) return;
    startX = e.touches[0].clientX;
    startY = e.touches[0].clientY;
    tracking = true;
  }, { passive: true });

  document.addEventListener('touchend', function (e) {
    if (!tracking) return;
    tracking = false;
    if (!e.changedTouches || !e.changedTouches.length) return;

    var dx = e.changedTouches[0].clientX - startX;
    var dy = e.changedTouches[0].clientY - startY;

    if (Math.abs(dx) < DISTANCE) return;
    if (Math.abs(dx) < Math.abs(dy) * SLOPE) return;       /* it was a scroll */

    /* Drag LEFT (negative dx) reveals the NEXT photo, matching every native carousel. */
    go(dx < 0 ? 1 : -1);
  }, { passive: true });

  /* A drag that leaves the element or is interrupted by a system gesture must not fire
     on the next touchend. */
  document.addEventListener('touchcancel', function () { tracking = false; }, { passive: true });
})();
